import React, { memo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Stack,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import VerifiedIcon from "@mui/icons-material/Verified";
import { CLINIC_INFO } from "../../data/siteData";
import banner from "../../assets/hero-banner.png";
const STATS = [
  { number: "10,000+", label: "Patients" },
  { number: "30+", label: "Years Exp." },
  { number: "5★", label: "Rating" },
  { number: "98%", label: "Satisfaction" },
];

// ── Stat badge — pill shape, not circle ──
const StatBadge = memo(({ number, label, showDivider }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-around",
    }}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: { xs: 1, md: 3 },
        py: { xs: 0.5, md: 1.5 },
      }}>
      <Typography
        sx={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontWeight: 600,
          fontSize: { xs: "1.25rem", sm: "1.8rem", md: "2rem" },
          lineHeight: 1,
          background: "linear-gradient(135deg, #7DDAC8, #EFFFFA)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          mb: 0.4,
        }}>
        {number}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"DM Sans", Arial, sans-serif',
          color: "rgba(255,255,255,0.76)",
          fontSize: { xs: "0.7rem", md: "0.78rem" },
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}>
        {label}
      </Typography>
    </Box>

    {/* Vertical divider between stats */}
    {showDivider && (
      <Box
        sx={{
          display: {
            xs: "none",
            sm: "block",
          },
          width: "1px",
          height: { xs: 32, md: 40 },
          background:
            "linear-gradient(to bottom, transparent, rgba(255,255,255,0.22), transparent)",
          flexShrink: 0,
        }}
      />
    )}
  </Box>
));

function HeroSection({ onBookClick }) {
  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      "Hello, I would like to book an appointment at Noble Dental Surgery.",
    );
    window.open(`https://wa.me/${CLINIC_INFO.whatsapp}?text=${msg}`, "_blank");
  };

  return (
    <>
      <Box
        component="section"
        aria-label="Welcome to Noble Dental Surgery"
        sx={{
          minHeight: "100vh",
          backgroundImage: `
      linear-gradient(
        90deg,
        rgba(7,18,32,0.72) 0%,
        rgba(7,18,32,0.58) 44%,
        rgba(7,18,32,0.42) 100%
      ),
      url(${banner})
    `,
          backgroundSize: "cover",
          backgroundPosition: {
            xs: "58% center",
            md: "center",
          },
          backgroundRepeat: "no-repeat",
          position: "relative",
          display: "flex",
          alignItems: "center",
          overflow: "hidden",

          pt: { xs: 10, md: 8 },
          pb: { xs: 8, md: 4 },

          // One-time zoom-in
          animation: "heroZoomIn 2.5s ease-out forwards",

          "@keyframes heroZoomIn": {
            from: {
              transform: "scale(1.08)",
            },
            to: {
              transform: "scale(1)",
            },
          },
        }}>
        {/* ── Decorative background blobs ── */}
        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Grid
            container
            spacing={{ xs: 6, md: 4, lg: 6 }}
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
            }}>
            {/* ════════════════════════════════
              LEFT — Copy block
          ════════════════════════════════ */}
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              {/* Trust badge */}
              <Chip
                icon={<VerifiedIcon sx={{ fontSize: "1rem !important" }} />}
                label="Your perfect smile awaits"
                sx={{
                  mb: { xs: 3, md: 4 },
                  bgcolor: "rgba(255,255,255,0.14)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.28)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                  fontWeight: 600,
                  fontSize: { xs: "0.72rem", md: "0.8rem" },
                  height: 34,
                  "& .MuiChip-icon": { color: "#00C9A7" },
                }}
              />

              {/* Headline */}
              <Typography
                variant="h1"
                sx={{
                  color: "#fff",
                  fontSize: {
                    xs: "2.6rem",
                    // sm: "3.2rem",
                  },
                  fontWeight: 700,
                  lineHeight: 1.12,
                  mb: 0.5,
                  textShadow: "0 8px 28px rgba(0,0,0,0.38)",
                  animation: "hUp 0.65s ease both",
                  "@keyframes hUp": {
                    from: { opacity: 0, transform: "translateY(22px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}>
                AI-Powered Smart Dental Clinic
              </Typography>

              <Typography
                variant="h1"
                sx={{
                  fontSize: {
                    xs: "2.6rem",
                    // sm: "3.2rem",
                  },
                  fontWeight: 500,
                  lineHeight: 1.12,
                  mb: { xs: 3, md: 3.5 },
                  background:
                    "linear-gradient(135deg, #00C9A7 0%, #00897B 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.35))",
                  animation: "hUp 0.65s 0.1s ease both",
                }}>
                First of Its kind in Hoshiarpur
              </Typography>

              {/* Sub-copy */}
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255,255,255,0.88)",
                  fontSize: { xs: "1rem", sm: "1.08rem", md: "1.18rem" },
                  fontWeight: 500,
                  lineHeight: 1.75,
                  mb: { xs: 4, md: 5 },
                  maxWidth: 560,
                  textShadow: "0 4px 18px rgba(0,0,0,0.35)",
                  animation: "hUp 0.65s 0.2s ease both",
                }}>
                Experience Dentistry Redefined — Noble Dental Surgery combines
                the ScanO AI Diagnostic Robot, 3Shape Intraoral Scanner, OPG,
                and CBCT along with specialist care, delivering instant reports,
                digital smile designing, and precision treatment that matches
                global dentistry standards.
              </Typography>

              {/* CTA buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  mb: { xs: 5, md: 6 },
                  animation: "hUp 0.65s 0.3s ease both",
                }}>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<CalendarMonthIcon />}
                  onClick={onBookClick}
                  sx={{
                    fontSize: { xs: "0.95rem", md: "1rem" },
                    px: { xs: 3.5, md: 4 },
                    py: 1.6,
                    fontWeight: 700,
                    color: "#071220",
                    boxShadow: "0 10px 28px rgba(0,201,167,0.28)",
                  }}>
                  Book Appointment
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} />}
                  onClick={handleWhatsApp}
                  sx={{
                    fontSize: { xs: "0.95rem", md: "1rem" },
                    px: { xs: 3.5, md: 4 },
                    py: 1.6,
                    fontWeight: 700,
                    borderColor: "rgba(255,255,255,0.65)",
                    borderWidth: "1.5px",
                    color: "#071220",
                    bgcolor: "rgba(255,255,255,0.94)",
                    "&:hover": {
                      borderColor: "#fff",
                      borderWidth: "1.5px",
                      bgcolor: "#fff",
                      boxShadow: "0 8px 24px rgba(255,255,255,0.22)",
                    },
                  }}>
                  Chat on WhatsApp
                </Button>
              </Stack>

              {/* ── Stats row ── */}
              <Box
                sx={{
                  animation: "hUp 0.65s 0.4s ease both",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(4, auto)",
                  },
                  alignItems: "center",
                  borderRadius: 3,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(7,18,32,0.42)",
                  boxShadow: "0 16px 42px rgba(0,0,0,0.22)",
                  overflow: "hidden",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  width: "100%",
                  // width: { xs: '100%', sm: 'auto' },
                }}>
                {STATS.map((s, i) => (
                  <StatBadge
                    key={s.label}
                    {...s}
                    showDivider={i < STATS.length - 1}
                  />
                ))}
              </Box>
            </Grid>

            {/* ════════════════════════════════
              RIGHT — Visual circle
          ════════════════════════════════ */}
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default memo(HeroSection);
